import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const STAGES = [
  {
    stage: "01 — TOP OF FUNNEL",
    title: "Awareness & Education",
    sub: "Content Syndication · MQL Opt-Ins",
    stat: "142K",
    statLabel: "WEEKLY REACH",
    width: "100%",
    accent: "#6D9CFF",
  },
  {
    stage: "02 — MIDDLE OF FUNNEL",
    title: "Evaluation & Intent",
    sub: "HQL · Webinars · Nurturing",
    stat: "38K",
    statLabel: "ACTIVELY ENGAGED",
    width: "78%",
    accent: "#2563EB",
    label: "#8FB2FF",
  },
  {
    stage: "03 — BOTTOM OF FUNNEL",
    title: "Decision & Commitment",
    sub: "BANT · SQL · Confirmed Call-Backs",
    stat: "6.2K",
    statLabel: "SALES-READY",
    width: "62%",
    accent: "#F59E0B",
    label: "#F59E0B",
  },
];

const EASE = [0.22, 1, 0.36, 1];

export const FunnelVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [1, -1]), { stiffness: 50, damping: 22 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-1.5, 1.5]), { stiffness: 50, damping: 22 });
  const [verified, setVerified] = useState(48210);

  useEffect(() => {
    const id = setInterval(() => setVerified((v) => v + Math.floor(Math.random() * 3) + 1), 3200);
    return () => clearInterval(id);
  }, []);

  const onMove = (e) => {
    if (typeof window !== "undefined" && !window.matchMedia("(pointer: fine)").matches) return;
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative flex h-[500px] select-none items-center sm:h-[560px]"
      style={{ perspective: 1400 }}
      data-testid="hero-funnel-visual"
    >
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        className="mx-auto flex w-full max-w-[540px] flex-col items-center"
      >
        {/* status bar — verified + live intent, integrated as the funnel source */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 1 }}
          className="w-full rounded-xl border border-white/[0.09] bg-[#0e1e33]/70 backdrop-blur-sm"
        >
          <div className="flex items-center justify-between px-5 py-3">
            <div className="flex items-baseline gap-2.5" data-testid="chip-verified-today">
              <span className="font-mono2 text-[9px] tracking-[0.2em] text-slate-500">VERIFIED TODAY</span>
              <span className="font-jakarta text-base font-bold tabular-nums text-white">
                {verified.toLocaleString()}
              </span>
            </div>
            <span
              className="flex items-center gap-1.5 font-mono2 text-[9px] tracking-[0.2em] text-slate-400"
              data-testid="chip-intent-live"
            >
              INTENT SIGNAL{" "}
              <span className="live-pulse text-emerald-400">
                ●
              </span>{" "}
              LIVE
            </span>
          </div>
        </motion.div>
        <div className="h-5 w-px bg-white/[0.08]" />

        {/* funnel panels */}
        {STAGES.map((s, i) => (
          <div key={s.stage} className="flex w-full flex-col items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + i * 0.18, duration: 0.8, ease: EASE }}
              className="rounded-xl border border-white/[0.09] bg-[#0e1e33]/70 shadow-[0_10px_28px_-24px_rgba(2,6,17,0.7)] backdrop-blur-sm"
              style={{ width: s.width }}
            >
              <div className="flex items-center gap-4 px-5 py-[18px]">
                <span className="h-9 w-[3px] shrink-0 rounded-full" style={{ background: s.accent }} />
                <div className="min-w-0 flex-1">
                  <p className="font-mono2 text-[9px] tracking-[0.22em]" style={{ color: s.label || s.accent }}>
                    {s.stage}
                  </p>
                  <p className="mt-1 font-jakarta text-sm font-bold text-white">{s.title}</p>
                  <p className="mt-0.5 truncate text-[11px] text-slate-400">{s.sub}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-jakarta text-lg font-extrabold tabular-nums text-white">{s.stat}</p>
                  <p className="font-mono2 text-[8px] tracking-[0.18em] text-slate-500">{s.statLabel}</p>
                </div>
              </div>
            </motion.div>
            {i < STAGES.length - 1 && <div className="h-5 w-px bg-white/[0.08]" />}
          </div>
        ))}

        {/* confirmed outcome strip — conversion link from BoFu */}
        <div className="h-6 w-px bg-flame/30" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8, ease: EASE }}
          className="w-[62%] cursor-default rounded-xl border border-flame/20 bg-flame/[0.05] transition-colors duration-500 hover:border-flame/40 hover:bg-flame/[0.09]"
          data-testid="chip-bant-qualified"
        >
          <div className="flex items-center gap-3 px-5 py-3.5">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-flame" />
            <div className="min-w-0">
              <p className="font-mono2 text-[8px] tracking-[0.22em] text-flame">BANT QUALIFIED</p>
              <p className="mt-0.5 truncate text-xs font-semibold text-white">
                Callback confirmed · Q3 budget
              </p>
            </div>
          </div>
        </motion.div>

        {/* journey caption */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-6 font-mono2 text-[9px] tracking-[0.26em] text-slate-500"
        >
          AWARENESS → EVALUATION → DECISION → CONFIRMED CONVERSATION
        </motion.p>
      </motion.div>
    </div>
  );
};
